import Decimal from 'decimal.js';

export const dollarsToCents = (dollars: number): number => {
  const cents = new Decimal(dollars).mul(100);
  if (!cents.isInteger()) {
    throw new Error(`dollarsToCents received a sub-cent value: ${dollars}`);
  }
  return cents.toNumber();
};

export const centsToDollars = (cents: number): number => {
  const value = new Decimal(cents);
  if (!value.isInteger()) {
    throw new Error(`centsToDollars received a non-integer value: ${cents}`);
  }
  return value.div(100).toNumber();
};

/* eslint-disable sonarjs/super-linear-regex */
export const formatCurrencyCompactEnUs = (
  amount: number,
  { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}
) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

type CurrencyAmount = number | Decimal | null;

export const formatCurrencyEnUs = (
  amount?: CurrencyAmount,
  { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}
) => {
  const number =
    typeof amount === 'number' ? amount : (amount?.toNumber() ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number);
};

export const formatCurrencyEnUsWithNoCentsAndAlwaysMaximumFractionDigits = (
  amount?: CurrencyAmount,
  { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}
) => {
  const number =
    typeof amount === 'number' ? amount : (amount?.toNumber() ?? 0);

  const isInteger = Number.isInteger(number);

  const adjustedMinFractionDigits = isInteger
    ? minimumFractionDigits
    : maximumFractionDigits;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: adjustedMinFractionDigits,
    maximumFractionDigits,
  }).format(number);
};

export const formatCurrencyEnUsWithCents = (amount?: CurrencyAmount) => {
  return formatCurrencyEnUs(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatCurrencyEnUsNoCents = (amount: number) => {
  return formatCurrencyEnUs(amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatCentsAsCurrencyEnUs = (amountInCents: number) => {
  return formatCurrencyEnUsWithCents(centsToDollars(amountInCents));
};

export const valueIsNaN = (value: string) => {
  return Number.isNaN(+value) || Number.isNaN(Number.parseFloat(value));
};

export const formatPotentialNumber = (value: string) => {
  if (valueIsNaN(value)) {
    return value;
  }

  return formatCurrencyEnUs(Number.parseFloat(value));
};

export const formatStringAmountToUSD = (amount: string) => {
  return valueIsNaN(amount)
    ? ''
    : `$${amount}`.replaceAll(/\B(?=(\d{3})+(?!\d))/gu, ',');
};

export const formatToFixedFloat = (value?: number | null, precision = 2) => {
  if (value === 0) {
    return 0;
  }
  return value ? Number.parseFloat(value.toFixed(precision)) : null;
};

export const parseStringToFloat = (amount?: string): number => {
  return Number.parseFloat(amount?.replaceAll(/[$,]/gu, '') || '');
};

/** Matches what the `formatCurrency*` helpers produce, e.g. `$1,000` or `-$100`. */
const CURRENCY_PATTERN = /^-?\$/u;

export const isCurrencyString = (value: unknown): value is string =>
  typeof value === 'string' && CURRENCY_PATTERN.test(value);

/**
 * The number behind a formatted currency string, for sorting table columns that
 * hold display strings rather than numbers. Comparing those as text would order
 * `$1,000` before `$900`. Values that are not currency sort as the smallest.
 */
export const parseCurrencyForSort = (value: unknown): number =>
  isCurrencyString(value)
    ? parseStringToFloat(value)
    : Number.NEGATIVE_INFINITY;

export function parseNumberToFloat(numberString: string | number) {
  if (typeof numberString === 'string') {
    return Number.parseFloat(numberString);
  }
  return numberString;
}
